import { forwardRef, useCallback } from 'react';
import Avatar from '@mui/material/Avatar';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faArrowTurnUp } from '@fortawesome/free-solid-svg-icons';

import AvatarGroup from '../../../AvatarGroup';
import Colours from '../../../../Theme/Colours';
// import { CallMedium } from '../../../../Enums/ActivityEnum';
import Typography, { TypographyVariant } from '../../../Typography';
import { CallStatusBadgeStatusType } from '../../../CallStatusBadge';
import DropdownButton, { DropdownButtonSplitTheme } from '../../../Split';
import CallActionAvatar, { CallActionAvatarType, CallActionAvatarVariant, CallActionAvatarSize } from '../../../CallActionAvatar'

import {
  Styles, CallHistoryRowDiv,
  ColumnActionsDiv, DurationDiv, DropdownButtonDiv,
  ContactNameTimestampContainer, ContactNameDiv, TimestampDiv
} from './CallHistoryRow.theme';
import { CallHistoryRowProps, CallDirection, StatusType } from './CallHistoryRow.types';

const StatusTypeIconMap = {
  [StatusType.inboundMissed]: <FontAwesomeIcon style={{ fontSize: '14px' }} icon={faArrowTurnUp  as IconProp} color='#FF1A1A' flip='horizontal' />,
  [StatusType.inboundReceived]: <FontAwesomeIcon style={{ fontSize: '14px' }} icon={faArrowUp  as IconProp} color='#E469E8' transform={{ rotate: 225 }} />,
  [StatusType.outboundMissed]: <FontAwesomeIcon style={{ fontSize: '14px' }} icon={faArrowTurnUp  as IconProp} color='#FF1A1A' />,
  [StatusType.outboundReceived]: <FontAwesomeIcon style={{ fontSize: '14px' }} icon={faArrowUp  as IconProp} color='#5D2DCD' transform={{ rotate: 45 }} />,
};
const colourMap = {
  [StatusType.inboundMissed]: '#FFE8E8',
  [StatusType.inboundReceived]: '#E9E9EE',
  [StatusType.outboundMissed]: '#FFE8E8',
  [StatusType.outboundReceived]: '#F1F1FF',
};

const CallHistoryRowComponent = forwardRef<HTMLDivElement, CallHistoryRowProps<any>>((props, ref) => {
  const {
    id,
    style,
    callFailed,
    callDirection,
    // callMedium,
    contactName,
    date, time,
    duration,
    // avatarClassName,
    actions,
    getActionNode,
    showActionsOverflowTooltip,
    getActionsOverflowCountTooltipContent,
    showCallActionButton,
    callUnwrapped,
    item,
    menuListStyle,
    callButtonDisabled,
    getMenuItems, onCall, onHistoryRowClick, onAvatarClick,
  } = props;

  const callIsInbound = callDirection === CallDirection.inbound;
  // const isSalesforceCall = callMedium === CallMedium.salesForce;
  // const isRocketCellCall = callMedium === CallMedium.rocketCell;

  let callStatusType = CallStatusBadgeStatusType.inboundReceived;
  if (callFailed) callStatusType = callIsInbound ? CallStatusBadgeStatusType.inboundMissed : CallStatusBadgeStatusType.outboundMissed;
  else callStatusType = callIsInbound ? CallStatusBadgeStatusType.inboundReceived : CallStatusBadgeStatusType.outboundReceived;
  
  const iconMap = StatusTypeIconMap[callStatusType];

  const handleDefaultCallClick = useCallback((cb:any, e:any) => {
    e.stopPropagation();
    onCall(item);
  }, [onCall, item]);

  const handleHistoryRowClick = () => {
    onHistoryRowClick?.(item);
  };
  const handleAvatarClick = (e:any) => {
    e.stopPropagation();
    onAvatarClick?.(item);
  };

  return (
    <CallHistoryRowDiv
      id={id}
      ref={ref}
      style={style}
      onClick={handleHistoryRowClick}
    >
      <Avatar sx={Styles.avatar} style={{ backgroundColor: colourMap[callStatusType] ?? Colours.PrimaryB.normal }}>
          {iconMap}
        </Avatar>
      <ContactNameTimestampContainer>
        <ContactNameDiv id={`${id}-Contact-Name-Div`} onClick={handleAvatarClick}>
          <Typography.H3
            id={`${id}-Contact-Name`}
            className={Styles.contactNameText}
            variant={TypographyVariant.active}
            noWrap
          >
            {contactName}
          </Typography.H3>
        </ContactNameDiv>
        <TimestampDiv>
          <div id={`${id}-Timestamp-Date`}><Typography.H3
            id={`${id}-Contact-Name`}
            className={Styles.timestamp}
            variant={TypographyVariant.active}
            noWrap
          >
            {date}
          </Typography.H3></div>
          <div id={`${id}-Timestamp-Time`} style={{ marginLeft: 12 }}>
            <Typography.H3
              id={`${id}-Contact-Name`}
              className={Styles.timestamp}
              variant={TypographyVariant.active}
              noWrap
            >
              {time}
            </Typography.H3>
          </div>
        </TimestampDiv>
      </ContactNameTimestampContainer>

      <ColumnActionsDiv>
        {!callFailed && callUnwrapped && (
          <CallActionAvatar
            id={`${id}-ActionIncompleted`}
            key={`${id}-ActionIncompleted`}
            className={Styles.flagIcon}
            size={CallActionAvatarSize.small}
            type={CallActionAvatarType.incomplete}
            variant={CallActionAvatarVariant.normal}
          />
        )}

        {actions && (
          <AvatarGroup
            id={`${id}-ActionsGroup`}
            key={`${id}-ActionsGroup`}
            data={actions}
            item={item}
            getAvatarNode={getActionNode}
            max={2}
            overflowCountHasTooltip={showActionsOverflowTooltip}
            getOverflowCountTolltipContent={getActionsOverflowCountTooltipContent}
          />
        )}
      </ColumnActionsDiv>
      <DurationDiv className="durationDiv"  durationWithCallButtonClass={showCallActionButton ? Styles.columnDurationWithCallButton : {}}>
        <Typography.P1
          id={`${id}-Duration`}
          className={Styles.durationText}
          variant={TypographyVariant.active}
          noWrap
        >
          {duration}
        </Typography.P1>
      </DurationDiv>

      <DropdownButtonDiv className="dropdownButtonDiv"  showCallActionButton={showCallActionButton}>
        <DropdownButton
          id={`${id}-CallActionButton`}
          menuListStyle={menuListStyle}
          theme={DropdownButtonSplitTheme.secondary}
          disabled={callButtonDisabled}
          menuItems={getMenuItems(item)}
          onPrimaryButtonClick={handleDefaultCallClick}
        >
          Call
        </DropdownButton>
      </DropdownButtonDiv>
    </CallHistoryRowDiv>
  );
});

CallHistoryRowComponent.defaultProps = {
  style: undefined,
  callFailed: false,
  callUnwrapped: false,
  showNavigationNode: false,
  showCallActionButton: false,
  showNavNodeOnHover: false,
  navigationNode: undefined,  //may cause problem   () => null
  callButtonDisabled: false,
  onCall: () => null,
  onHistoryRowClick: () => null,
  onAvatarClick: () => null,
};

export {
  CallDirection as CallHistoryCallDirection,
} from './CallHistoryRow.types';
export default CallHistoryRowComponent;
